// Hideo - the script editor's shell (src/components/dialogs/ScriptEditor.vue): opening and closing, the title, the
// Visual / Markdown / Fountain switch, the save status and the autosave, the counter, the tabs bar, Copy and the .md export.
// Ported from the legacy editor/scripts.js and editor.js (setScriptMode, queueScriptSave, updScriptStats, copyScript,
// exportScript) with the same order of steps (the Fountain mode is new: its text field and panels are
// src/components/editor/FountainEditor.vue, its logic src/features/editor/fountain*.ts): the writing surface (#scriptBody and its menus, src/features/editor/surface/)
// reads the page's visibility and the selection, so they must be in the same state at the same moment as before.
// The data side is the scripts store (open script, saveText, tabs, templates).
import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { t, tv } from '../i18n'
import { useDataStore } from './data'
import { useScriptsStore } from './scripts'
import { useDialogsStore } from './dialogs'
import { useVideoStore } from './video'
import { useTocStore } from './toc'
import { useCommentsStore } from './comments'
import { useToolbarStore } from './toolbar'
import { activeTabOf } from '../features/scripts'
import { formatKind } from '../data/contentTypes'
import { cleanHtml, edBodyEl, fixLinks, fixTodos, liftLists, noZw, stripLowc } from '../features/editor/html'
import { fixContrast } from '../features/editor/colors'
import { flattenBlocks, tidyStyles } from '../features/editor/tidy'
import { htmlToMd, mdToHtml } from '../features/editor/markdown'
import { fountainStats, scriptStats } from '../features/editor/stats'
import { fountainFromPage, fountainPrompterHtml, fountainTabHtml, fountainToHtml } from '../features/editor/fountainHtml'
import { downloadFountain, loadScreenplayFont, printScreenplay, type Paper } from '../features/editor/fountainPrint'
import { loadEditorFonts } from '../features/editor/styles'
import { refreshChips } from '../features/mentions'
import { byId, setSavedRange } from '../features/editor/surface/core'
import { edHideFloating } from '../features/editor/surface/floating'
import { hideSelMenu } from '../features/editor/surface/selmenu'   // also forgets an open colour picker (selPicking), or the menu could stay later
import type { DialogEntry } from '../features/dialogs'
import type { Script } from '../data/types'
import { app } from '../features/app'
import { downloadText } from '../features/desktop'

export type EditorMode = 'visual' | 'md' | 'fountain'
/** The mode a script opens in: an unknown or missing one is the visual mode */
export const modeOf = (m: unknown): EditorMode => m === 'md' || m === 'fountain' ? m : 'visual'
/** A tab of the tabs bar as it was drawn */
interface TabView { id: string; name: string; on: boolean }

const mdEl = () => byId('scriptMd') as HTMLTextAreaElement
const fnEl = () => byId('scriptFountain') as HTMLTextAreaElement

export const useEditorStore = defineStore('editor', () => {
  const scripts = useScriptsStore()
  /** On screen (#scriptEditorModal without .hidden: ScriptEditor.vue follows it at once) */
  const shown = ref(false)
  /** The editor's mode */
  const mode = ref<EditorMode>('visual')
  /** The mode the page shows: page and toolbar, or the Markdown field (ScriptEditor.vue follows it at once). It changes
   *  where the legacy setScriptMode switched the elements, after the outline was drawn */
  const shownMode = ref<EditorMode>('visual')
  /** The title field */
  const title = ref('')
  /** The status at the top (Saved, Saving…, Copied!…) */
  const status = ref(t('Saved'))
  /** The counter at the bottom */
  const stats = ref(t('0 words'))
  /** The tabs bar as last drawn; tabsSeq grows at every drawing (the tabs are drawn again, with their entrance) */
  const tabs = shallowRef<TabView[]>([])
  const tabsSeq = ref(0)
  /** The tab being renamed in place, and its field */
  const renaming = ref<{ id: string; value: string; seq: number } | null>(null)
  /** Grows when the store writes the Fountain field (opening, tab, mode): FountainEditor.vue reads it again */
  const fountainSeq = ref(0)
  const setFountain = (src: string) => { fnEl().value = src; fountainSeq.value++ }
  let timer: ReturnType<typeof setTimeout> | undefined, fixTimer: ReturnType<typeof setTimeout> | undefined, renameSeq = 0

  // ----- The dialog stack: the editor is registered while it is on screen (Esc and Ctrl+S are its steps in src/features/keyboard.ts; the
  // online data is not loaded over it) -----
  const dialogs = useDialogsStore()
  const entry: DialogEntry = { name: 'editor', esc: false, editing: true, close: () => closeEditor() }
  function setShown(on: boolean): void {
    if (shown.value === on) return
    shown.value = on
    if (on) { dialogs.show('editor', {}); dialogs.register(entry) }
    else { dialogs.unregister(entry); dialogs.hide('editor') }
  }

  // ----- Text, counter, saving -----
  /** The editor's plain text: the Markdown, the Fountain text, or the page's text without invisible spaces */
  const scriptText = (): string => mode.value === 'md' ? mdEl().value : mode.value === 'fountain' ? fnEl().value : noZw(edBodyEl().innerText)
  /** Words and time under the page (Blog: reading time; Fountain: pages and screen time) */
  function updStats(): void {
    if (mode.value === 'fountain') { stats.value = fountainStats(fnEl().value); return }
    const d = useDataStore().getData(), sc = d.scripts.find(x => x.id === scripts.editingId), v = sc && d.videos.find(x => x.id === sc.videoId)
    stats.value = scriptStats(scriptText(), !!(v && formatKind(v.format) === 'blog'))
  }
  /** What the editor holds for the open tab: the title, the HTML without display marks and invisible spaces (Markdown
   *  mode: the Markdown turned into HTML; Fountain mode: the Fountain text as one code block), the mode */
  const editorText = () => ({
    title: title.value,
    html: mode.value === 'md' ? cleanHtml(mdToHtml(mdEl().value)) : mode.value === 'fountain' ? fountainTabHtml(fnEl().value) : noZw(stripLowc(edBodyEl().innerHTML)),
    mode: mode.value
  })
  /** The checks after a change: old checkboxes, colours unreadable on the theme (a just-picked colour too), the outline
   *  and the word count. They go over the whole page, so queueSave runs them once the typing pauses (150 ms), not at
   *  every key; save runs them first when they are still waiting */
  function afterChange(): void {
    clearTimeout(fixTimer); fixTimer = undefined
    if (mode.value === 'visual') { fixTodos(); fixContrast(edBodyEl()); useTocStore().queue() }
    updStats()
  }
  /** Saves the open tab now (Ctrl+S, before closing, switching tab…) */
  function save(): void {
    if (fixTimer) afterChange()
    clearTimeout(timer)
    if (scripts.saveText(editorText)) status.value = t('Saved')
  }
  /** Something changed: saved 600 ms after the last change */
  function queueSave(): void {
    status.value = t('Saving the script…')
    clearTimeout(fixTimer); fixTimer = setTimeout(afterChange, 150)
    clearTimeout(timer); timer = setTimeout(save, 600)
  }

  // ----- Visual / Markdown / Fountain -----
  /** Switches mode (silent: while opening, no conversion and no save). Between Markdown and Fountain the text goes
   *  through the page (Markdown → page → Fountain) */
  function setMode(m: EditorMode, silent?: boolean): void {
    const body = edBodyEl(), md = mdEl()
    if (!silent) {
      if (m === mode.value) return
      if (mode.value === 'fountain') { body.innerHTML = cleanHtml(fountainToHtml(fnEl().value)); flattenBlocks(body); tidyStyles(body); fixContrast(body) }
      if (m === 'md') md.value = htmlToMd(body)
      else if (m === 'fountain') { if (mode.value === 'md') { body.innerHTML = cleanHtml(mdToHtml(md.value)); flattenBlocks(body) } setFountain(fountainFromPage(body)) }
      else if (mode.value === 'md') { body.innerHTML = cleanHtml(mdToHtml(md.value)); flattenBlocks(body); tidyStyles(body); fixContrast(body) }
    } else if (m === 'md') md.value = htmlToMd(body)
    else if (m === 'fountain') setFountain(fountainFromPage(body))
    mode.value = m
    const notVisual = m !== 'visual'
    if (m === 'fountain') loadScreenplayFont()
    if (notVisual) edHideFloating()
    else { useTocStore().render(); setTimeout(() => useToolbarStore().layout()) }
    shownMode.value = m
    updStats()
    if (!silent) queueSave()
  }

  // ----- Opening, tabs, closing -----
  /** Draws the tabs bar */
  function renderTabs(sc: Script): void {
    tabs.value = (sc.tabs || []).map(tab => ({ id: tab.id, name: String(tv(tab.name)), on: tab.id === sc.activeTab }))
    renaming.value = null
    tabsSeq.value++
  }
  /** The open tab's text in the page: cleaned, mention names brought up to date, the browser's stray styles and the
   *  colours unreadable on the theme fixed */
  function showTabHtml(sc: Script): void {
    const body = edBodyEl()
    body.innerHTML = cleanHtml((activeTabOf(sc) || { html: '' }).html || ''); flattenBlocks(body); refreshChips(body); tidyStyles(body); fixContrast(body)
  }
  /** After the text is shown: the tabs, old lists and checkboxes and links fixed, outline, comments, styles, top of the page */
  function afterShow(sc: Script): void {
    renderTabs(sc); liftLists(); fixTodos(); fixLinks(); useTocStore().render(); useCommentsStore().render(); useToolbarStore().applyStyles(); byId('scriptVisualWrap').scrollTop = 0
  }
  /** Shows the open tab of the script */
  function loadTab(sc: Script): void {
    showTabHtml(sc)
    if (mode.value === 'md') mdEl().value = htmlToMd(edBodyEl())
    else if (mode.value === 'fountain') setFountain(fountainFromPage(edBodyEl()))
    updStats(); afterShow(sc)
  }
  /** Opens a script */
  function openEditor(id: string): void {
    const sc = scripts.open(id)   // with its tabs
    if (!sc) return
    setSavedRange(null); loadEditorFonts()
    title.value = String(tv(sc.title) || ''); status.value = t('Saved'); mode.value = 'visual'
    setShown(true)
    showTabHtml(sc)
    setMode(modeOf(sc.mode), true); afterShow(sc)
    if (mode.value === 'visual') { edBodyEl().focus(); document.execCommand('styleWithCSS', false, 'true'); document.execCommand('defaultParagraphSeparator', false, 'p') }
  }
  /** Back: saves, closes and redraws (Esc: src/features/keyboard.ts) */
  function closeEditor(): void {
    save()
    setShown(false); hideSelMenu(); edHideFloating()
    scripts.close()
    app.renderAll()
    useVideoStore().pickScriptNow()   // the video dialog's script picker, if it is open
  }
  /** Deletes the open script after asking */
  async function removeScript(): Promise<void> {
    if (!(await scripts.remove())) return   // asks, deletes, the videos that chose it lose it
    clearTimeout(fixTimer); fixTimer = undefined
    setShown(false); hideSelMenu(); edHideFloating()
    app.renderAll()
    useVideoStore().pickScriptNow('')   // switch to another script of the video, if there is one
  }
  /** "Save as template" (the open tab) */
  async function saveTemplate(): Promise<void> {
    save()
    if (await scripts.saveTemplate(title.value)) status.value = t('Template saved')
  }
  /** Opens another tab (the text is saved first) */
  function goTab(id: string): void { save(); const sc = scripts.goTab(id); if (sc) loadTab(sc) }
  /** New tab (asks its name) */
  async function addTab(): Promise<void> { const sc = await scripts.newTab(save); if (sc) loadTab(sc) }
  /** Deletes a tab (asks first) */
  async function delTab(id: string): Promise<void> { const sc = await scripts.tabDelete(id, save); if (sc) loadTab(sc) }
  /** Rename in place: the name becomes a field (Enter or click outside = save, Esc = cancel), max 40 characters */
  function startRename(id: string): void {
    const cur = scripts.current()[1], tab0 = cur && (cur.tabs || []).find(x => x.id === id)
    if (!tab0 || !tabs.value.some(x => x.id === id) || (renaming.value && renaming.value.id === id)) return
    // Another tab's field open: it is saved (its blur did that before, and the tabs were drawn again without a field)
    if (renaming.value) return finishRename(renaming.value.seq, true)
    renaming.value = { id, value: String(tv(tab0.name)), seq: ++renameSeq }
  }
  /** The rename field is done (save = Enter or click outside); seq = the field's own number, so it ends only once */
  function finishRename(seq: number, save_: boolean): void {
    const r = renaming.value
    if (!r || r.seq !== seq) return
    renaming.value = null
    save()
    const sc = scripts.tabRename(r.id, save_ ? r.value : null)
    if (sc) renderTabs(sc)
  }

  // ----- Footer -----
  /** Copies the plain text */
  function copy(): void { navigator.clipboard.writeText(scriptText()).then(() => { status.value = t('Copied!') }) }
  /** Downloads the script as Markdown: "# Title" on top (an untitled script gets the name it is shown with) */
  function exportMd(): void {
    const md = mode.value === 'md' ? mdEl().value : htmlToMd(edBodyEl())
    const head = title.value.trim() ? title.value : t('Untitled script')
    downloadText((title.value.trim() || 'script') + '.md', '# ' + head + '\n\n' + md, 'text/markdown')
  }

  /** Fountain mode: downloads the .fountain file, prints the screenplay (PDF), the teleprompter's text */
  function exportFountain(): void { downloadFountain(fnEl().value, title.value) }
  function printPdf(paper: Paper): void { save(); printScreenplay(fnEl().value, title.value, paper) }
  const prompterHtml = (): string => mode.value === 'fountain' ? fountainPrompterHtml(fnEl().value) : editorText().html

  return {
    shown, mode, shownMode, title, status, stats, tabs, tabsSeq, renaming, fountainSeq,
    setShown, scriptText, updStats, editorText, save, queueSave, setMode, renderTabs, loadTab, openEditor, closeEditor,
    removeScript, saveTemplate, goTab, addTab, delTab, startRename, finishRename, copy, exportMd, exportFountain, printPdf, prompterHtml
  }
})
